import React, { FC, Fragment, useEffect, useState } from 'react';
import { Autocomplete, FormControl, TextField, TextFieldProps } from '@mui/material';
import { Country } from 'models/country';
import { useTranslation } from 'react-i18next';
import { getEnabledCountries } from 'utils/country-helper';

type CountryInputProps = {
    country?: Country;
    onCountrySelect: (country: Country) => void;
} & TextFieldProps;

const NO_COUNTRY_OPTION: Country = {
    name: 'NO_COUNTRY',
    alpha2Code: '',
    alpha3Code: '',
    organizationNumberPattern: null,
    organizationNumberFormat: null,
};
const CountryInput: FC<CountryInputProps> = ({ country, onCountrySelect, ...textFieldProps }: CountryInputProps) => {
    const { t: translate } = useTranslation(['orders', 'organization', 'country-names']);
    const EnabledCountries = getEnabledCountries();
    const countryOptions = !country ? [NO_COUNTRY_OPTION, ...EnabledCountries] : EnabledCountries;
    const [selectedCountry, setSelectedCountry] = useState<Country>(country || NO_COUNTRY_OPTION);

    useEffect(() => {
        if (country) {
            const foundCountry = EnabledCountries.find(
                (countryOption) => countryOption.alpha2Code === country.alpha2Code,
            );
            if (foundCountry) {
                setSelectedCountry(foundCountry);
            }
        }
    }, [country]);

    const handleCountryChange = (_: unknown, country: Country | null) => {
        if (country) {
            setSelectedCountry(country);
            onCountrySelect(country);
            return;
        }
        setSelectedCountry(NO_COUNTRY_OPTION);
        onCountrySelect(NO_COUNTRY_OPTION);
    };

    return (
        <Fragment>
            <FormControl fullWidth>
                <Autocomplete
                    fullWidth
                    autoComplete
                    filterOptions={(x) => x}
                    options={countryOptions}
                    getOptionLabel={(country: Country) =>
                        country.alpha2Code === ''
                            ? translate('country-names:NO_COUNTRY')
                            : translate(`country-names:COUNTRY_${country.alpha2Code}`)
                    }
                    getOptionKey={(country: Country) => country.alpha2Code}
                    isOptionEqualToValue={(a: Country, b: Country) => a.alpha2Code === b?.alpha2Code}
                    noOptionsText={translate('orders:TYPE_TO_SEARCH')}
                    includeInputInList
                    filterSelectedOptions
                    renderInput={(params) => (
                        <TextField {...params} {...textFieldProps} label={translate('organization:ADDRESS:COUNTRY')} />
                    )}
                    value={selectedCountry}
                    onChange={handleCountryChange}
                    renderOption={(props, option: Country) => {
                        const isDisabled = option.alpha2Code === '' && country;
                        return (
                            <li
                                {...props}
                                style={{ opacity: isDisabled ? 0.5 : 1, pointerEvents: isDisabled ? 'none' : 'auto' }}
                            >
                                {option.alpha2Code === ''
                                    ? translate('country-names:NO_COUNTRY')
                                    : translate(`country-names:COUNTRY_${option.alpha2Code}`)}
                            </li>
                        );
                    }}
                />
            </FormControl>
        </Fragment>
    );
};

export default CountryInput;
